// Import necessary React hooks
import { useState } from "react";
import { useNavigate } from "react-router-dom";
// Import necessary Material-UI components
import {
  Box,
  Dialog,
  TextField,
  Container,
  CssBaseline,
  Button,
  Typography,
  Grid,
} from "@mui/material";
// Import custom validation function and hooks
import { validateReceiverDetailsInput } from "../validation/validateReceiverDetailsInput";
import { LoadingButtonText } from "../../common";
import { handleSendReceiverData } from "../functions/handleSendReceiverData.function";

/**
 * SampleRequestSampleControlAddReceiverDetailsModal component handles adding receiver details
 * for sample requests with form validation and submission functionality.
 * @param {Object} props - Component props including modal state, data, and callbacks.
 */
export function SampleRequestSampleControlAddReceiverDetailsModal({
  open,
  setOpen,
  data,
  sampleRequestID,
  sampleItemID,
  supplierValues,
  display,
  setReloadData,
}) {
  // Navigation hook from React Router
  const history = useNavigate();
  // State for controlling form submission loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State for handling form validation errors
  const [errors, setErrors] = useState();
  // Close modal handler
  const handleClose = () => {
    setOpen(false);
  };

  // Form submission handler
  const handleSubmit = async (event) => {
    // Prevent default form submission behavior
    event.preventDefault();

    const formData = new FormData(event.currentTarget);

    // Extract form data into sampleDetails object
    const sampleDetails = {
      ...data,
      erpId: formData.get("erpId"),
      erpName: formData.get("erpName"),
    };

    // Validate form input and set errors if invalid
    const { validationErrors, isValid } =
      validateReceiverDetailsInput(sampleDetails);
    // Set validation errors if not valid
    !isValid && setErrors(validationErrors);
    // Clear errors if valid
    isValid && setErrors();
    // Send data if valid
    isValid &&
      (await handleSendReceiverData(
        setErrors,
        setIsSubmitting,
        supplierValues,
        data,
        sampleRequestID,
        sampleItemID,
        sampleDetails,
        setReloadData,
        history
      ));
  };
  // Log errors to console if they exist
  errors && console.log({ errors });

  return (
    <div>
      {/* Dialog for adding receiver details */}
      <Dialog open={open} onClose={handleClose} {...DialogProps}>
        <Box
          sx={{
            display: display,
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Container {...ContainerProps}>
            {/* Modal title */}
            <Typography component="h1" variant="h5">
              Add Receiver Details
            </Typography>
            <CssBaseline />
            <Box {...FormBoxProps} onSubmit={handleSubmit}>
              {/* Form for adding receiver details */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    required
                    fullWidth
                    id="erpName"
                    label="ERP Name"
                    name="erpName"
                    error={errors && errors.erpName && true}
                    helperText={
                      errors && errors.erpName !== "" ? errors.erpName : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    required
                    fullWidth
                    id="erpId"
                    label="ERP ID"
                    name="erpId"
                    error={errors && errors.erpId && true}
                    helperText={
                      errors && errors.erpId !== "" ? errors.erpId : ""
                    }
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button disabled={isSubmitting} {...SaveButtonProps}>
                  {/* Conditional rendering of button text or loading spinner */}
                  <LoadingButtonText loading={isSubmitting} text="Save" />
                </Button>
              </Grid>
            </Box>
          </Container>
        </Box>
      </Dialog>
    </div>
  );
}

const DialogProps = {
  style: {
    height: "90%",
  },
  PaperProps: { sx: { borderRadius: "20px" } },
};
const ContainerProps = {
  component: "main",
  maxWidth: "sm",
  sx: {
    marginTop: 2,
    flexDirection: "column",
    alignItems: "center",
  },
};
const FormBoxProps = {
  component: "form",
  noValidate: true,
  sx: {
    mt: 5,
  },
};
const SaveButtonProps = {
  type: "submit",
  variant: "contained",
  sx: {
    mt: 3,
    width: "150px",
    float: "right",
    mb: 3,
  },
};
